import type { Messages } from "@pharmacy/i18n";

/**
 * Dates and times, as the server sends them and as a person reads them
 * (the same output as apps/mobile/src/utils/dates.ts).
 *
 * The server sends moments as ISO strings in UTC ("2026-10-08T13:08:00.000Z")
 * and days as YYYY-MM-DD. People read DD/MM/YYYY and a 24-hour HH:mm.
 *
 * Always in the pharmacy's time zone, Africa/Mogadishu, not the computer's:
 * a laptop set to another zone must still show the shop's day. At 1am in
 * Mogadishu the UTC day is still yesterday, so cutting the first ten
 * characters off a timestamp would show the wrong day.
 */

export const PHARMACY_TIME_ZONE = "Africa/Mogadishu";

const pad = (n: number) => String(n).padStart(2, "0");

/** The pharmacy's calendar day and clock for a moment. */
const partsFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: PHARMACY_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

interface ZonedParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function zoned(at: Date): ZonedParts {
  const parts: Record<string, string> = {};
  for (const p of partsFormat.formatToParts(at)) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
  };
}

/** A local calendar date as YYYY-MM-DD. `month` is 0–11, as in JavaScript. */
export function toIso(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/** YYYY-MM-DD into its parts, month 0–11. Null for anything else. */
export function parseIso(value: string | null | undefined): { year: number; month: number; day: number } | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}

/** The hour (0–23) on the pharmacy's clock: for "Good morning". `now` is for tests. */
export function hourNow(now: Date = new Date()): number {
  return zoned(now).hour;
}

/** A day `days` after YYYY-MM-DD `iso` (negative goes back). */
export function addDays(iso: string, days: number): string {
  const p = parseIso(iso);
  if (!p) return iso;
  const d = new Date(Date.UTC(p.year, p.month, p.day + days));
  return toIso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Today in the pharmacy, as YYYY-MM-DD. `now` is for tests. */
export function today(now: Date = new Date()): string {
  const z = zoned(now);
  return `${z.year}-${pad(z.month)}-${pad(z.day)}`;
}

/** The pharmacy's day of a moment (an ISO timestamp), as YYYY-MM-DD. */
export function dayOf(moment: string): string {
  return today(new Date(moment));
}

/**
 * For reading: 31/01/2027. Takes a day (an expiry) or a moment (when something
 * was recorded); a moment is first turned into the pharmacy's day. Returns what
 * it was given when that is not a date, rather than a blank.
 */
export function formatDate(value: string | null | undefined): string {
  if (value && value.includes("T")) {
    const at = new Date(value);
    if (!Number.isNaN(at.getTime())) {
      const z = zoned(at);
      return `${pad(z.day)}/${pad(z.month)}/${z.year}`;
    }
  }
  const parts = parseIso(value);
  if (!parts) return value ?? "";
  return `${pad(parts.day)}/${pad(parts.month + 1)}/${parts.year}`;
}

/** The time of day of a moment, on the pharmacy's clock: "14:32". */
export function formatTime(value: string): string {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "";
  const z = zoned(at);
  return `${pad(z.hour)}:${pad(z.minute)}`;
}

/** "Thursday 8 October", in the language in use (the phone's Home greeting). */
export function formatLongDate(day: string, t: Pick<Messages, "weekdays" | "monthNames" | "longDate">): string {
  const parts = parseIso(day);
  if (!parts) return "";
  // A calendar day has no time zone: build it in UTC so the weekday is that day's.
  const date = new Date(Date.UTC(parts.year, parts.month, parts.day));
  // Monday first, as the names are.
  const weekday = t.weekdays[(date.getUTCDay() + 6) % 7] ?? "";
  return t.longDate(weekday, parts.day, t.monthNames[parts.month] ?? "");
}

/** Days in a month, `month` 0–11. Day 0 of the next month is this month's last. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Where the 1st of the month falls in a week that starts on Monday: 0 Monday … 6 Sunday. */
export function mondayIndex(year: number, month: number): number {
  return (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
}
